import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Upload, Download, Loader2, CheckCircle2, AlertCircle, FileText,
} from "lucide-react";
import { apiPost } from "@/lib/api";
import { toast } from "sonner";

const CSV_TEMPLATE = `Symbol,Shares,PurchasePrice,PurchaseDate
COMB.N0000,500,205.00,2026-01-15
HNB.X0000,1000,340.00,2026-02-10
DIAL.N0000,2000,32.50,2026-03-01
`;

/**
 * ImportCsvDialog — Phase 7 Stage 9 Part E.
 *
 * Two-step flow:
 *   1. Pick a CSV file → validate (dry_run=true)
 *   2. Show validation table → confirm → import (dry_run=false)
 */
const ImportCsvDialog = ({ open, onClose, portfolioId, onImported }) => {
  const [file, setFile] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setFile(null);
    setCsvText("");
    setValidation(null);
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const onPickFile = async (f) => {
    if (!f) return;
    setFile(f);
    const text = await f.text();
    setCsvText(text);
    setValidation(null);
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cse_holdings_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const validate = async () => {
    if (!csvText.trim()) {
      toast.error("Pick a CSV file first");
      return;
    }
    setBusy(true);
    try {
      const res = await apiPost(
        `/portfolios/${portfolioId}/import-csv?dry_run=true`,
        { csv_text: csvText },
      );
      setValidation(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
    } finally {
      setBusy(false);
    }
  };

  const importNow = async () => {
    setBusy(true);
    try {
      const res = await apiPost(
        `/portfolios/${portfolioId}/import-csv?dry_run=false`,
        { csv_text: csvText },
      );
      const ok = (res.imported || []).length;
      toast.success(`✅ ${ok} holding${ok === 1 ? "" : "s"} imported`, {
        description: `to your portfolio`,
      });
      onImported?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="import-csv-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-[hsl(var(--primary))]" />
            Import existing holdings
          </DialogTitle>
          <DialogDescription>
            Upload a CSV with columns:{" "}
            <code className="font-mono text-[11px] bg-muted px-1 rounded">
              Symbol, Shares, PurchasePrice, PurchaseDate
            </code>
            . Each row is validated against your CSE securities database before importing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
              className="gap-1.5"
              data-testid="csv-template-btn"
            >
              <Download className="h-3.5 w-3.5" /> 📥 Download CSV template
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="csv-file" className="text-xs">
              CSV file
            </Label>
            <input
              id="csv-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => onPickFile(e.target.files?.[0])}
              className="block w-full text-xs file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:text-primary-foreground hover:file:bg-primary/90"
              data-testid="csv-file-input"
            />
            {file && (
              <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" /> {file.name} ·{" "}
                {(file.size / 1024).toFixed(1)} KB
              </div>
            )}
          </div>

          {!validation && (
            <Button
              onClick={validate}
              disabled={busy || !csvText.trim()}
              className="gap-2"
              data-testid="csv-validate-btn"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Validate rows
            </Button>
          )}

          {validation && (
            <div className="space-y-2">
              <div className="text-sm font-display font-semibold">
                Validation results: {validation.valid_count} of {validation.total_rows} valid
                {validation.error_count > 0 && (
                  <span className="text-[hsl(var(--destructive))] ml-1">
                    · {validation.error_count} error{validation.error_count === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <div className="rounded-md border max-h-[280px] overflow-y-auto">
                <table className="w-full text-xs" data-testid="csv-validation-table">
                  <thead className="bg-muted/30 border-b text-[10px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Row</th>
                      <th className="px-2 py-1.5 text-left">Symbol</th>
                      <th className="px-2 py-1.5 text-right">Shares</th>
                      <th className="px-2 py-1.5 text-right">Price</th>
                      <th className="px-2 py-1.5 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(validation.valid_rows || []).map((r) => (
                      <tr key={`v-${r.row}`} className="border-b last:border-0">
                        <td className="px-2 py-1.5">{r.row}</td>
                        <td className="px-2 py-1.5 font-mono">{r.cse_symbol}</td>
                        <td className="px-2 py-1.5 text-right tabular">
                          {r.shares_purchased.toLocaleString()}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular">
                          {r.purchase_price.toFixed(2)}
                        </td>
                        <td className="px-2 py-1.5">
                          <Badge variant="outline" className="text-[10px] gap-1 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30">
                            <CheckCircle2 className="h-3 w-3" /> Valid
                          </Badge>
                        </td>
                      </tr>
                    ))}
                    {(validation.errors || []).map((r, i) => (
                      <tr
                        key={`e-${r.row}-${i}`}
                        className="border-b last:border-0 bg-destructive/5"
                      >
                        <td className="px-2 py-1.5">{r.row}</td>
                        <td className="px-2 py-1.5 font-mono">{r.symbol}</td>
                        <td className="px-2 py-1.5 text-right">—</td>
                        <td className="px-2 py-1.5 text-right">—</td>
                        <td className="px-2 py-1.5">
                          <Badge variant="outline" className="text-[10px] gap-1 bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30">
                            <AlertCircle className="h-3 w-3" />{" "}
                            {(r.errors || []).join(", ")}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>
            Cancel
          </Button>
          {validation && validation.valid_count > 0 && (
            <Button onClick={importNow} disabled={busy} data-testid="csv-import-btn">
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}
              Import {validation.valid_count} valid row
              {validation.valid_count === 1 ? "" : "s"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportCsvDialog;
